import { useEffect, useState } from 'preact/hooks';
import { del, get, media, patch, post } from '../api';
import { AssetLibrary } from '../pages/Account';
import { Workers } from './Workers';
import { Button, Card, Countdown, Empty, Field, Loading, Modal, Pill, Tabs, timeAgo, toast, useLoad } from '../ui';
import { MapView } from './MapView';
import { Feed } from './Play';
import type { GameSocket } from './socket';

type Panel = 'table' | 'world' | 'story' | 'cast' | 'directives' | 'llm' | 'settings' | 'art';

export function GMTab(p: { sock: GameSocket }) {
	const gm = p.sock.gm.use();
	const [panel, setPanel] = useState<Panel>('table');
	useEffect(() => {
		void p.sock.gmOp('state');
	}, []);
	if (!gm) return <Loading />;
	const approvals = gm.scenes.filter((s: any) => s.state === 'awaiting_approval').length;
	const proposedChanges = gm.changesets.filter((c: any) => c.status === 'proposed').length;
	return (
		<div class="gm">
			<Tabs
				value={panel}
				onChange={setPanel}
				tabs={[
					{ id: 'table', label: 'Table', badge: approvals + gm.joinRequests.length },
					{ id: 'world', label: 'World' },
					{ id: 'story', label: 'Story' },
					{ id: 'cast', label: 'Cast' },
					{ id: 'directives', label: 'Directives', badge: proposedChanges },
					{ id: 'llm', label: 'Models' },
					{ id: 'settings', label: 'Settings' },
					{ id: 'art', label: 'Art' },
				]}
			/>
			<div class="gm-body">
				{panel === 'table' ? <TablePanel sock={p.sock} gm={gm} /> : null}
				{panel === 'world' ? <World sock={p.sock} gm={gm} /> : null}
				{panel === 'story' ? <Story sock={p.sock} gm={gm} /> : null}
				{panel === 'cast' ? <Cast sock={p.sock} gm={gm} /> : null}
				{panel === 'directives' ? <Directives sock={p.sock} gm={gm} /> : null}
				{panel === 'llm' ? <Models sock={p.sock} gm={gm} /> : null}
				{panel === 'settings' ? <Settings sock={p.sock} gm={gm} /> : null}
				{panel === 'art' ? <Art sock={p.sock} gm={gm} /> : null}
			</div>
		</div>
	);
}

const nameOf = (gm: any, id: string) => gm.characters.find((c: any) => c.id === id)?.name ?? id;

// ------------------------------------------------------------------ table: live dashboard

function TablePanel(p: { sock: GameSocket; gm: any }) {
	const { gm, sock } = p;
	const passages = sock.gmPassages.use();
	const skew = sock.skew.use();
	return (
		<div class="gm-grid">
			<div class="gm-main">
				{gm.settings.status === 'lobby' ? (
					<Card title="Lobby">
						<p>Players claim characters, then you start. Characters left unclaimed run as NPCs driven by their goals.</p>
						<Button onClick={() => sock.gmOp('start')}>Start the game</Button>
					</Card>
				) : null}
				{gm.scenes.filter((s: any) => s.state === 'awaiting_approval').map((s: any) => (
					<Approval key={s.area} sock={sock} gm={gm} scene={s} />
				))}
				<Card title="Scenes">
					{gm.scenes.length ? (
						gm.scenes.map((s: any) => {
							const chars = gm.characters.filter((c: any) => c.area === s.area && c.claimedBy);
							return (
								<div key={s.area} class="scene-row">
									<strong>{gm.spec.areas.find((a: any) => a.id === s.area)?.name ?? s.area}</strong> <Pill tone={s.state === 'collecting' ? 'ok' : 'accent'}>{s.state}</Pill> beat {s.beat_no}
									<ul class="list small">
										{chars.map((c: any) => {
											const timer = gm.timers.find((t: any) => t.character_id === c.id && t.kind === 'action');
											const acted = gm.actions.find((a: any) => a.character_id === c.id);
											return (
												<li key={c.id} class="row-between">
													<span>
														{c.name}: {acted ? <em>{acted.text ?? JSON.parse(acted.intents).map((i: any) => i.verb).join(', ')}</em> : timer?.draft ? <span class="muted">typing “{timer.draft.slice(0, 60)}”</span> : <span class="muted">thinking…</span>}
													</span>
													{timer ? <Countdown deadline={timer.deadline} skew={-skew} total={gm.settings.timerSeconds} /> : null}
												</li>
											);
										})}
									</ul>
								</div>
							);
						})
					) : (
						<Empty>No active scenes.</Empty>
					)}
					{gm.proposals.length ? (
						<>
							<h4>Open questions</h4>
							<ul class="list small">
								{gm.proposals.map((pr: any) => (
									<li key={pr.id}>
										{pr.summary} — {Object.entries(pr.answers).map(([k, v]) => `${nameOf(gm, k)}: ${v}`).join(', ')}
									</li>
								))}
							</ul>
						</>
					) : null}
					<Button small variant="ghost" onClick={() => sock.gmOp('advance_time')}>
						Advance time one beat
					</Button>
				</Card>
				<Card title="Everything that happens (GM view, with mechanics)">
					<Feed passages={passages} empty="Narration appears here as beats resolve." />
				</Card>
			</div>
			<div class="gm-side">
				<Players sock={sock} gm={gm} />
				<Card title="Feed">
					<ul class="list small feed-list">
						{[...gm.feed].reverse().map((f: any) => (
							<li key={f.seq}>
								<Pill tone={f.kind === 'warning' ? 'warn' : f.kind === 'improv' ? 'accent' : 'muted'}>{f.kind}</Pill> {f.data.message ?? `${f.data.name} appeared in ${f.data.area}: ${f.data.description ?? ''}`} <span class="muted">{timeAgo(f.at)}</span>
							</li>
						))}
					</ul>
				</Card>
			</div>
		</div>
	);
}

function Approval(p: { sock: GameSocket; gm: any; scene: any }) {
	const [text, setText] = useState(JSON.stringify(p.scene.pending.adjudications, null, 2));
	const approve = async (reject = false) => {
		let adjudications;
		try {
			adjudications = JSON.parse(text);
		} catch {
			return toast('That JSON does not parse.', 'error');
		}
		await p.sock.gmOp('approve', { area: p.scene.area, adjudications, reject });
	};
	return (
		<Card title={`Approve beat in ${p.scene.area}`} class="card-alert">
			<ul class="list small">
				{p.scene.pending.items.map((i: any) => (
					<li key={i.key}>
						<code>{i.key}</code> {i.summary}
					</li>
				))}
			</ul>
			<Field label="Adjudications (edit before approving; the engine still re-checks every effect)">
				<textarea class="mono" rows={10} value={text} onInput={(e) => setText(e.currentTarget.value)} />
			</Field>
			<div class="row">
				<Button onClick={() => approve(false)}>Approve and resolve</Button>
				<Button variant="ghost" onClick={() => approve(true)}>
					Reject (the moment passes)
				</Button>
			</div>
		</Card>
	);
}

function Players(p: { sock: GameSocket; gm: any }) {
	const { gm, sock } = p;
	return (
		<Card title="Players">
			<ul class="list">
				{gm.players.map((pl: any) => (
					<li key={pl.userId}>
						<div class="row-between">
							<span>
								<span class={`dot ${pl.online ? 'dot-on' : ''}`} /> {pl.name} {pl.characterName ? <span class="muted">as {pl.characterName}</span> : null} {pl.status === 'observing' ? <Pill>observing</Pill> : null}
							</span>
							{pl.role !== 'gm' ? (
								<span class="row">
									<button class="linkish small" onClick={() => confirm(`Kick ${pl.name}? Their character becomes an NPC.`) && sock.gmOp('kick', { userId: pl.userId, ban: false, mode: 'npc' })}>
										kick
									</button>
									<button class="linkish small danger" onClick={() => confirm(`Ban ${pl.name} from this game?`) && sock.gmOp('kick', { userId: pl.userId, ban: true, mode: 'unclaimed' })}>
										ban
									</button>
								</span>
							) : (
								<Pill tone="accent">GM</Pill>
							)}
						</div>
					</li>
				))}
			</ul>
			{gm.joinRequests.length ? (
				<>
					<h4>Requests</h4>
					<ul class="list">
						{gm.joinRequests.map((r: any) => (
							<li key={r.user_id} class="row-between">
								<span>
									{r.display_name}: {r.kind === 'join' ? 'wants to join' : r.kind === 'character' ? `original character “${r.data.name}” — ${r.data.description}` : `switch to ${nameOf(gm, r.data.character)}`}
								</span>
								<span class="row">
									<Button small onClick={() => sock.gmOp('join_decision', { userId: r.user_id, accept: true })}>
										Allow
									</Button>
									<Button small variant="quiet" onClick={() => sock.gmOp('join_decision', { userId: r.user_id, accept: false })}>
										Deny
									</Button>
								</span>
							</li>
						))}
					</ul>
				</>
			) : null}
		</Card>
	);
}

// ------------------------------------------------------------------ world: map + overrides

function World(p: { sock: GameSocket; gm: any }) {
	const { gm, sock } = p;
	const [sel, setSel] = useState<string | null>(gm.characters.find((c: any) => c.claimedBy)?.area ?? gm.spec.areas[0].id);
	const tokens: Record<string, string[]> = {};
	for (const c of gm.characters) if (!c.removed) (tokens[c.area] ??= []).push(c.claimedBy ? `★${c.name}` : c.name);
	const exits = gm.spec.exits.map((x: any) => ({ ...x, state: gm.exits[x.id]?.state ?? x.state, lock: x.key || x.lockCondition ? '1' : undefined }));
	const area = gm.spec.areas.find((a: any) => a.id === sel);
	const here = gm.characters.filter((c: any) => c.area === sel && !c.removed);
	const items = gm.items.filter((i: any) => i.place.kind === 'area' && i.place.id === sel);
	const fx = (effects: any[], note = 'GM override') => sock.gmOp('override', { note, effects });
	return (
		<div class="gm-grid">
			<div class="gm-main">
				<Card title="Story map">
					<MapView areas={gm.spec.areas} exits={exits} tokens={tokens} selected={sel} onSelect={setSel} />
					<p class="small muted">★ player characters. Dashed edges are hidden or destroyed exits; 🔑 marks exits with a key or lock condition.</p>
				</Card>
				<Overrides sock={sock} gm={gm} area={sel} />
			</div>
			<div class="gm-side">
				{area ? (
					<Card title={area.name}>
						<p class="small">{area.description}</p>
						<p class="small">State: {JSON.stringify(gm.areas[area.id]?.state ?? {})}</p>
						<h4>Here</h4>
						<ul class="list small">
							{here.map((c: any) => (
								<li key={c.id}>
									<strong>{c.name}</strong> {c.claimedBy ? <Pill tone="accent">player</Pill> : <Pill>{c.origin === 'improvised' ? 'improvised' : 'npc'}</Pill>} {c.conditions.map((x: string) => <Pill key={x} tone="warn">{x}</Pill>)}
									<br />
									<span class="muted">holds {c.inventory.join(', ') || 'nothing'}</span>
								</li>
							))}
						</ul>
						<h4>Items</h4>
						<p class="small">{items.map((i: any) => i.id).join(', ') || 'none'}</p>
						<h4>Exits</h4>
						<ul class="list small">
							{exits
								.filter((x: any) => x.from === sel || x.to === sel)
								.map((x: any) => (
									<li key={x.id} class="row-between">
										<span>
											{x.name} → {x.from === sel ? x.to : x.from} <Pill>{x.state}</Pill>
										</span>
										<select value={x.state} onChange={(e) => fx([{ type: 'set_exit', exit: x.id, state: e.currentTarget.value }], `exit ${x.id}`)} aria-label={`State of ${x.name}`}>
											{['open', 'locked', 'hidden', 'removed'].map((s) => (
												<option key={s}>{s}</option>
											))}
										</select>
									</li>
								))}
						</ul>
					</Card>
				) : null}
			</div>
		</div>
	);
}

function Overrides(p: { sock: GameSocket; gm: any; area: string | null }) {
	const { gm, sock } = p;
	const chars = gm.characters.filter((c: any) => !c.removed);
	const [who, setWho] = useState(chars[0]?.id ?? '');
	const [to, setTo] = useState(gm.spec.areas[0].id);
	const [item, setItem] = useState(gm.items[0]?.id ?? '');
	const [cond, setCond] = useState('');
	const [flag, setFlag] = useState('');
	const [flagVal, setFlagVal] = useState('true');
	const [narr, setNarr] = useState('');
	const [npc, setNpc] = useState(chars.find((c: any) => !c.claimedBy)?.id ?? '');
	const [words, setWords] = useState('');
	const [raw, setRaw] = useState('[\n  { "type": "narrate", "hint": "A cold wind rises.", "area": "' + (p.area ?? '') + '" }\n]');
	const fx = (effects: any[], note: string) => sock.gmOp('override', { note, effects });
	const charSelect = (v: string, set: (s: string) => void, filter?: (c: any) => boolean) => (
		<select value={v} onChange={(e) => set(e.currentTarget.value)}>
			{chars.filter(filter ?? (() => true)).map((c: any) => (
				<option key={c.id} value={c.id}>
					{c.name}
				</option>
			))}
		</select>
	);
	return (
		<Card title="Override anything (logged as GMOverride events; undo with rewind)">
			<div class="override-grid">
				<div>
					<h4>Character</h4>
					{charSelect(who, setWho)}
					<div class="row wrap">
						<select value={to} onChange={(e) => setTo(e.currentTarget.value)} aria-label="Area">
							{gm.spec.areas.map((a: any) => (
								<option key={a.id} value={a.id}>
									{a.name}
								</option>
							))}
						</select>
						<Button small variant="ghost" onClick={() => fx([{ type: 'move_character', who, area: to }], `move ${who}`)}>
							Move there
						</Button>
					</div>
					<div class="row wrap">
						<input placeholder="condition" value={cond} onInput={(e) => setCond(e.currentTarget.value)} />
						<Button small variant="ghost" disabled={!cond} onClick={() => fx([{ type: 'apply_condition', who, condition: cond, beats: 5 }], 'condition')}>
							Apply (5 beats)
						</Button>
						<Button small variant="quiet" disabled={!cond} onClick={() => fx([{ type: 'remove_condition', who, condition: cond }], 'condition')}>
							Remove
						</Button>
					</div>
					<div class="row wrap">
						<Button small variant="ghost" onClick={() => fx([{ type: 'wound', who }], 'wound')}>
							Wound
						</Button>
						<Button small variant="ghost" onClick={() => fx([{ type: 'heal', who }], 'heal')}>
							Heal
						</Button>
						<Button small variant="ghost" onClick={() => fx([{ type: 'grant_xp', who, amount: 5 }], 'xp')}>
							+5 XP
						</Button>
					</div>
				</div>
				<div>
					<h4>Item</h4>
					<select value={item} onChange={(e) => setItem(e.currentTarget.value)}>
						{gm.items.map((i: any) => (
							<option key={i.id} value={i.id}>
								{i.id} ({i.place.kind} {i.place.id})
							</option>
						))}
					</select>
					<div class="row wrap">
						<Button small variant="ghost" onClick={() => fx([{ type: 'move_item', item, place: { kind: 'character', id: who } }], `give ${item}`)}>
							Give to {nameOf(gm, who)}
						</Button>
						<Button small variant="ghost" onClick={() => fx([{ type: 'move_item', item, place: { kind: 'area', id: to } }], `place ${item}`)}>
							Place in area
						</Button>
					</div>
					<h4>Flag</h4>
					<div class="row wrap">
						<input placeholder="flag key" value={flag} onInput={(e) => setFlag(e.currentTarget.value)} list="flag-keys" />
						<datalist id="flag-keys">
							{[...Object.keys(gm.flags), ...gm.spec.judgedFlags.map((f: any) => f.key)].map((k) => (
								<option key={k} value={k} />
							))}
						</datalist>
						<input value={flagVal} onInput={(e) => setFlagVal(e.currentTarget.value)} class="short" />
						<Button small variant="ghost" disabled={!flag} onClick={() => fx([{ type: 'set_flag', key: flag, value: flagVal === 'true' ? true : flagVal === 'false' ? false : isNaN(Number(flagVal)) ? flagVal : Number(flagVal), reason: 'GM' }], `flag ${flag}`)}>
							Set
						</Button>
					</div>
				</div>
				<div>
					<h4>Narrate</h4>
					<textarea rows={2} value={narr} onInput={(e) => setNarr(e.currentTarget.value)} placeholder="Something happening in the world…" />
					<div class="row wrap">
						<Button small variant="ghost" disabled={!narr || !p.area} onClick={() => sock.gmOp('narrate', { area: p.area, text: narr }).then(() => setNarr(''))}>
							To the selected area
						</Button>
						<Button small variant="ghost" disabled={!narr} onClick={() => sock.gmOp('narrate', { to: [who], text: narr }).then(() => setNarr(''))}>
							Only to {nameOf(gm, who)}
						</Button>
					</div>
					<h4>Speak as an NPC</h4>
					{charSelect(npc, setNpc, (c) => !c.claimedBy)}
					<div class="row wrap">
						<input placeholder="Words" value={words} onInput={(e) => setWords(e.currentTarget.value)} />
						<Button small variant="ghost" disabled={!words} onClick={() => sock.gmOp('npc_say', { npc, words }).then(() => setWords(''))}>
							Say
						</Button>
					</div>
				</div>
			</div>
			<details>
				<summary>Raw effects (JSON)</summary>
				<textarea class="mono" rows={6} value={raw} onInput={(e) => setRaw(e.currentTarget.value)} />
				<Button
					small
					onClick={() => {
						try {
							fx(JSON.parse(raw), 'raw GM effects');
						} catch {
							toast('That JSON does not parse.', 'error');
						}
					}}
				>
					Apply
				</Button>
			</details>
		</Card>
	);
}

// ------------------------------------------------------------------ story: arcs, pivots, outcome map, triggers

function condText(c: any): string {
	if (!c) return '';
	if (c.all) return c.all.map(condText).join(' AND ');
	if (c.any) return `(${c.any.map(condText).join(' OR ')})`;
	if (c.not) return `NOT ${condText(c.not)}`;
	const [k, v] = Object.entries(c)[0] as [string, any];
	return typeof v === 'object' ? `${k}(${Object.values(v).map((x) => (typeof x === 'object' ? JSON.stringify(x) : x)).join(', ')})` : `${k} ${v}`;
}
export { condText };

function Story(p: { sock: GameSocket; gm: any }) {
	const { gm, sock } = p;
	const [ending, setEnding] = useState('');
	return (
		<div class="gm-grid">
			<div class="gm-main">
				<Card title="Arcs">
					<div class="arc-board">
						{gm.spec.arcs.map((a: any) => {
							const st = gm.arcs[a.id];
							return (
								<div key={a.id} class="arc-col">
									<h4>
										{a.name} {st?.completed ? <Pill tone="ok">complete</Pill> : null}
									</h4>
									{a.stages.map((s: any) => (
										<div key={s.id} class={`stage ${st?.stage === s.id ? 'stage-on' : ''}`}>
											<strong>{s.name}</strong>
											{s.final ? <Pill>final</Pill> : null}
											<p class="small">{s.description}</p>
											{s.transitions.map((t: any, i: number) => (
												<p key={i} class="small muted">
													→ {t.to} when {condText(t.when)}
												</p>
											))}
											{s.deadline ? <p class="small muted">deadline {s.deadline.beats} beats</p> : null}
										</div>
									))}
								</div>
							);
						})}
					</div>
				</Card>
				<Card title="Pivots">
					<ul class="list">
						{gm.spec.pivots.map((pv: any) => {
							const st = gm.pivots[pv.id];
							return (
								<li key={pv.id}>
									<strong>{pv.question}</strong> <span class="muted small">owner {nameOf(gm, pv.owner)}</span>{' '}
									{st?.branch ? <Pill tone="ok">{st.branch} ({st.by})</Pill> : st?.openedAt !== null && st?.openedAt !== undefined ? <Pill tone="accent">open</Pill> : <Pill>not yet</Pill>}
									{st?.probabilities ? <p class="small muted">odds: {Object.entries(st.probabilities).map(([b, v]: any) => `${b} ${Math.round(v * 100)}%`).join(', ')}</p> : null}
								</li>
							);
						})}
					</ul>
				</Card>
				<Card title="Triggers">
					<ul class="list small">
						{gm.spec.triggers.map((t: any) => {
							const suppressed = gm.settings.suppressedTriggers.includes(t.id);
							return (
								<li key={t.id} class="row-between">
									<span>
										<code>{t.id}</code> {t.mode} — when {condText(t.when)} {suppressed ? <Pill tone="warn">suppressed</Pill> : null}
									</span>
									<span class="row">
										<button class="linkish" onClick={() => sock.gmOp('force_trigger', { id: t.id })}>
											force
										</button>
										<button class="linkish" onClick={() => sock.gmOp('suppress_trigger', { id: t.id, on: !suppressed })}>
											{suppressed ? 'allow' : 'suppress'}
										</button>
									</span>
								</li>
							);
						})}
					</ul>
				</Card>
			</div>
			<div class="gm-side">
				<Card title="Outcome map">
					<ul class="list">
						{gm.outcome.map((e: any) => (
							<li key={e.id}>
								<strong>{e.title}</strong> {e.reached ? <Pill tone="ok">reached</Pill> : e.reachable ? <Pill tone="accent">reachable</Pill> : <Pill tone="bad">closed</Pill>}
								{e.needed.length ? <p class="small muted">needs {e.needed.map((n: any) => `${n.pivot}=${n.branch}`).join(', ')}</p> : null}
							</li>
						))}
					</ul>
					<div class="row">
						<select value={ending} onChange={(e) => setEnding(e.currentTarget.value)} aria-label="Ending">
							<option value="">Closest ending</option>
							{gm.spec.endings.map((e: any) => (
								<option key={e.id} value={e.id}>
									{e.title}
								</option>
							))}
						</select>
						<Button small variant="danger" onClick={() => confirm('End the game now?') && sock.gmOp('declare_ending', { ending: ending || undefined })}>
							Declare ending
						</Button>
					</div>
				</Card>
				<AddEnding sock={sock} gm={gm} />
				<Card title="Flags">
					<ul class="list small">
						{Object.entries(gm.flags).map(([k, f]: any) => (
							<li key={k}>
								<code>{k}</code> = {String(f.value)} <span class="muted">({f.setBy}{f.reason ? `: ${f.reason}` : ''})</span>
							</li>
						))}
					</ul>
				</Card>
			</div>
		</div>
	);
}

function AddEnding(p: { sock: GameSocket; gm: any }) {
	const [title, setTitle] = useState('');
	const [brief, setBrief] = useState('');
	const [reqs, setReqs] = useState<Record<string, string>>({});
	const add = async () => {
		const id = `e_${title.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40)}`;
		const r = await p.sock.gmOp('add_ending', { ending: { id, title, brief, requires: { pivots: Object.fromEntries(Object.entries(reqs).filter(([, v]) => v)), flags: {} }, winners: [], losers: [], original: false } });
		if (r.ok) {
			toast('Ending added (new spec version for this campaign).', 'ok');
			setTitle('');
			setBrief('');
		}
	};
	return (
		<Card title="Add an ending">
			<Field label="Title">
				<input value={title} onInput={(e) => setTitle(e.currentTarget.value)} />
			</Field>
			<Field label="Closing brief">
				<textarea rows={2} value={brief} onInput={(e) => setBrief(e.currentTarget.value)} />
			</Field>
			{p.gm.spec.pivots.map((pv: any) => (
				<Field key={pv.id} label={pv.question}>
					<select value={reqs[pv.id] ?? ''} onChange={(e) => setReqs({ ...reqs, [pv.id]: e.currentTarget.value })}>
						<option value="">any</option>
						{pv.branches.map((b: any) => (
							<option key={b.id} value={b.id}>
								{b.label}
							</option>
						))}
					</select>
				</Field>
			))}
			<Button small disabled={!title || !brief} onClick={add}>
				Add ending
			</Button>
		</Card>
	);
}

// ------------------------------------------------------------------ cast: characters, improv, traits

function Cast(p: { sock: GameSocket; gm: any }) {
	const { gm, sock } = p;
	const [editing, setEditing] = useState<any>(null);
	return (
		<Card title="Characters">
			<table class="table">
				<thead>
					<tr>
						<th>Name</th>
						<th>Where</th>
						<th>Stats</th>
						<th>Status</th>
						<th>Traits</th>
						<th />
					</tr>
				</thead>
				<tbody>
					{gm.characters
						.filter((c: any) => !c.removed)
						.map((c: any) => (
							<tr key={c.id}>
								<td>
									<strong>{c.name}</strong>
									<br />
									<span class="muted small">
										{c.role} {c.origin === 'improvised' ? <Pill tone="accent">improvised</Pill> : null} {c.claimedBy ? <Pill tone="ok">player</Pill> : c.playable ? <Pill>unclaimed</Pill> : null}
									</span>
								</td>
								<td>{c.area}</td>
								<td class="small">
									M{c.stats.might} A{c.stats.agility} W{c.stats.wits} H{c.stats.heart} · lvl {c.level} · att {c.attitude}
								</td>
								<td class="small">
									{c.alive ? '' : 'dead '}
									{c.conditions.join(', ')} {c.wounds ? `· ${c.wounds} wounds` : ''}
								</td>
								<td class="small">
									{c.traits.map((t: any) => (
										<div key={t.id}>
											{t.summary} {t.source === 'player' ? <Pill>player</Pill> : null}
											{t.source !== 'spec' && !t.cardId ? (
												<>
													{' '}
													<button class="linkish" onClick={() => sock.gmOp('trait_upgrade', { character: c.id, trait: t.id, card: { suit: 'heart', value: 1 } })}>
														+1 card
													</button>{' '}
													<button class="linkish" onClick={() => sock.gmOp('trait_upgrade', { character: c.id, trait: t.id, card: { suit: null, value: -2, kind: 'wound' } })}>
														wound card
													</button>{' '}
													<button class="linkish danger" onClick={() => sock.gmOp('trait_veto', { character: c.id, trait: t.id })}>
														veto
													</button>
												</>
											) : null}
										</div>
									))}
								</td>
								<td class="small">
									{!c.claimedBy ? (
										<>
											<button class="linkish" onClick={() => sock.gmOp('release_npc', { character: c.id, on: !c.releasedForPlay })}>
												{c.releasedForPlay ? 'unrelease' : 'release for play'}
											</button>
											<br />
											<button class="linkish" onClick={() => sock.gmOp('npc_mode', { character: c.id, mode: c.npcMode === 'frozen' ? 'goals' : 'frozen' })}>
												{c.npcMode === 'frozen' ? 'unfreeze' : 'freeze'}
											</button>
										</>
									) : null}
									{c.origin === 'improvised' ? (
										<>
											<br />
											<button class="linkish" onClick={() => setEditing(c)}>
												edit
											</button>{' '}
											<button class="linkish" onClick={() => sock.gmOp('improv_promote', { character: c.id }).then((r: any) => r.ok && toast('Promoted into the story spec.', 'ok'))}>
												promote
											</button>{' '}
											<button class="linkish danger" onClick={() => sock.gmOp('improv_remove', { character: c.id })}>
												remove
											</button>
										</>
									) : null}
								</td>
							</tr>
						))}
				</tbody>
			</table>
			{editing ? <EditNpc sock={sock} c={editing} onClose={() => setEditing(null)} /> : null}
		</Card>
	);
}

function EditNpc(p: { sock: GameSocket; c: any; onClose: () => void }) {
	const [f, setF] = useState({ name: p.c.name, description: p.c.description, role: p.c.role, disposition: p.c.disposition ?? '', want: p.c.want ?? '' });
	return (
		<Modal title={`Edit ${p.c.name}`} onClose={p.onClose}>
			{(['name', 'role', 'disposition', 'want'] as const).map((k) => (
				<Field key={k} label={k}>
					<input value={f[k]} onInput={(e) => setF({ ...f, [k]: e.currentTarget.value })} />
				</Field>
			))}
			<Field label="description">
				<textarea rows={3} value={f.description} onInput={(e) => setF({ ...f, description: e.currentTarget.value })} />
			</Field>
			<Button onClick={() => p.sock.gmOp('improv_edit', { character: p.c.id, patch: f }).then(p.onClose)}>Save</Button>
		</Modal>
	);
}

// ------------------------------------------------------------------ directives and the living story

const WHEN_LABEL: Record<string, string> = {
	immediate: 'Immediate — narrated on the next beat',
	scheduled: 'Scheduled — when its trigger fires',
	when_matters: 'When it matters — next time the NPC acts on it',
	at_pivot: 'At a pivot — when the pivot resolves',
	long_term: 'Long-term — edits this campaign’s spec',
};

function Directives(p: { sock: GameSocket; gm: any }) {
	const { gm, sock } = p;
	const [text, setText] = useState('');
	const [scopeChars, setScopeChars] = useState<string[]>([]);
	const [busy, setBusy] = useState(false);
	const [current, setCurrent] = useState<string | null>(null);
	const [follow, setFollow] = useState('');
	const send = async (followUp?: string) => {
		setBusy(true);
		try {
			const r = await sock.gmOp('directive', { text: followUp ? follow : text, scope: { characters: scopeChars }, followUp: followUp ?? null });
			if (r.ok) {
				setCurrent(r.id);
				setFollow('');
			}
		} finally {
			setBusy(false);
		}
	};
	const cs = gm.changesets.find((c: any) => c.id === current) ?? gm.changesets.find((c: any) => c.status === 'proposed');
	return (
		<div class="gm-grid">
			<div class="gm-main">
				<Card title="Steer the story">
					<Field label="Directive" hint="Plain language. Nothing changes until you approve the preview.">
						<textarea rows={3} value={text} onInput={(e) => setText(e.currentTarget.value)} placeholder="Let the guard accidentally leave Mira's cell unlocked." />
					</Field>
					<Field label="Scope (optional)">
						<div class="row wrap">
							{gm.characters
								.filter((c: any) => !c.removed)
								.map((c: any) => (
									<label key={c.id} class="check small">
										<input type="checkbox" checked={scopeChars.includes(c.id)} onChange={(e) => setScopeChars(e.currentTarget.checked ? [...scopeChars, c.id] : scopeChars.filter((x) => x !== c.id))} /> {c.name}
									</label>
								))}
						</div>
					</Field>
					<Button onClick={() => send()} busy={busy} disabled={!text.trim()}>
						Draft change set
					</Button>
					{!gm.settings.llm.roles.planner.provider || gm.settings.llm.roles.planner.provider === 'none' ? <p class="small muted">Configure a Planner model in the Models tab first.</p> : null}
				</Card>
				{cs ? (
					<Card title={`Preview: ${cs.data.summary}`} class={cs.status === 'proposed' ? 'card-alert' : ''}>
						<p class="small">
							{cs.source === 'planner' ? 'Proposed by the living-story planner. ' : ''}Status: <Pill tone={cs.status === 'applied' ? 'ok' : cs.status === 'proposed' ? 'accent' : 'muted'}>{cs.status}</Pill>
						</p>
						{Object.keys(WHEN_LABEL).map((w) => {
							const changes = cs.data.changes.filter((c: any) => c.when === w);
							if (!changes.length) return null;
							return (
								<div key={w}>
									<h4>{WHEN_LABEL[w]}</h4>
									<ul class="list">
										{changes.map((c: any) => (
											<li key={c.id}>
												<strong>{c.description}</strong> {c.valid ? null : <Pill tone="bad">invalid</Pill>} {c.announce ? <Pill>announce</Pill> : <Pill>silent</Pill>}
												<p class="small muted">
													Who notices: {c.perceivedBy || '—'} · Narration: {c.narration || '—'}
												</p>
												{c.problems?.length ? <p class="small bad">{c.problems.join('; ')}</p> : null}
												<details>
													<summary class="small">mechanics{cs.status === 'proposed' ? ' (editable)' : ''}</summary>
													<ChangeEditor change={c} editable={cs.status === 'proposed'} onApprove={(edited) => sock.gmOp('changeset', { id: cs.id, action: 'approve', changeIds: [c.id], edits: [edited] })} />
												</details>
											</li>
										))}
									</ul>
								</div>
							);
						})}
						<p class="small">
							Endings reachable before: {cs.data.endingsBefore.join(', ') || 'none'} → after: {cs.data.endingsAfter.join(', ') || 'none'}
						</p>
						{cs.status === 'proposed' ? (
							<>
								<div class="row">
									<Button onClick={() => sock.gmOp('changeset', { id: cs.id, action: 'approve' })}>Approve all</Button>
									<Button variant="ghost" onClick={() => sock.gmOp('changeset', { id: cs.id, action: 'approve', changeIds: cs.data.changes.filter((c: any) => c.valid).map((c: any) => c.id) })}>
										Approve valid only
									</Button>
									<Button variant="quiet" onClick={() => sock.gmOp('changeset', { id: cs.id, action: 'deny' })}>
										Deny
									</Button>
								</div>
								<div class="row">
									<input value={follow} onInput={(e) => setFollow(e.currentTarget.value)} placeholder="Follow-up: e.g. make it subtler" />
									<Button variant="ghost" disabled={!follow} busy={busy} onClick={() => send(cs.id)}>
										Redraft
									</Button>
								</div>
							</>
						) : null}
					</Card>
				) : null}
			</div>
			<div class="gm-side">
				<Card title="Story changes timeline">
					<ul class="list small">
						{gm.changesets.map((c: any) => (
							<li key={c.id}>
								<button class="linkish" onClick={() => setCurrent(c.id)}>
									{c.data.summary}
								</button>{' '}
								<Pill tone={c.status === 'applied' ? 'ok' : c.status === 'proposed' ? 'accent' : 'muted'}>{c.status}</Pill> <span class="muted">{c.source} · {timeAgo(c.created_at)}</span>
								{c.status === 'applied' ? (
									<>
										{' '}
										<button class="linkish danger" onClick={() => confirm('Revert this change set? This rewinds the log to just before it.') && sock.gmOp('revert_changeset', { id: c.id })}>
											revert
										</button>
									</>
								) : null}
							</li>
						))}
					</ul>
					<p class="small muted">Re-planning mode: {gm.settings.replan} (change in Settings).</p>
				</Card>
			</div>
		</div>
	);
}

/** Edit one planned change's mechanics before approving just that change. */
function ChangeEditor(p: { change: any; editable: boolean; onApprove: (c: any) => void }) {
	const { valid: _v, problems: _p, ...rest } = p.change;
	const [text, setText] = useState(JSON.stringify(rest, null, 1));
	if (!p.editable) return <pre class="mono small">{text}</pre>;
	return (
		<>
			<textarea class="mono small" rows={8} value={text} onInput={(e) => setText(e.currentTarget.value)} />
			<Button
				small
				variant="ghost"
				onClick={() => {
					try {
						p.onApprove(JSON.parse(text));
					} catch {
						toast('That JSON does not parse.', 'error');
					}
				}}
			>
				Approve this change as edited
			</Button>
		</>
	);
}

// ------------------------------------------------------------------ models, worker, inspector

const PROVIDERS = [
	['none', 'None (templates / buttons only)'],
	['openai', 'OpenAI (your key)'],
	['openrouter', 'OpenRouter (your key)'],
	['xai', 'xAI Grok (your key)'],
	['anthropic', 'Anthropic (your key)'],
	['workers-ai', 'Included models (Storyteller / Saga Master)'],
	['local', 'Local worker on your machine'],
	['demo', 'Demo model (dev servers only)'],
];
const ROLES = ['interpreter', 'adjudicator', 'narrator', 'planner', 'compiler'] as const;

function Models(p: { sock: GameSocket; gm: any }) {
	const { gm, sock } = p;
	const [roles, setRoles] = useState(gm.settings.llm.roles);
	const [decider, setDecider] = useState(gm.settings.llm.decider);
	const [models, setModels] = useState<Record<string, string[]>>({});
	const [secret, setSecret] = useState<string | null>(null);
	const [inspect, setInspect] = useState<any>(null);
	const loadModels = async (provider: string) => {
		if (models[provider] || ['none', 'local'].includes(provider)) return;
		try {
			const r = await get(`/api/models?provider=${provider}`);
			setModels((m) => ({ ...m, [provider]: r.models }));
		} catch (e) {
			toast((e as Error).message, 'warn');
		}
	};
	const save = () => sock.gmOp('settings', { patch: { llm: { roles, decider } } }).then(() => toast('Model setup saved.', 'ok'));
	const w = gm.worker;
	return (
		<div class="gm-grid">
			<div class="gm-main">
				<Card title="Model per role" actions={<Button small onClick={save}>Save</Button>}>
					<table class="table">
						<tbody>
							{ROLES.map((r) => (
								<tr key={r}>
									<td>
										<strong>{r}</strong>
									</td>
									<td>
										<select
											value={roles[r].provider}
											onChange={(e) => {
												const provider = e.currentTarget.value;
												setRoles({ ...roles, [r]: { ...roles[r], provider } });
												void loadModels(provider);
											}}
										>
											{PROVIDERS.map(([v, l]) => (
												<option key={v} value={v}>
													{l}
												</option>
											))}
										</select>
									</td>
									<td>
										<input list={`models-${roles[r].provider}`} value={roles[r].model} onFocus={() => loadModels(roles[r].provider)} onInput={(e) => setRoles({ ...roles, [r]: { ...roles[r], model: e.currentTarget.value } })} placeholder="model id" />
										<datalist id={`models-${roles[r].provider}`}>
											{(models[roles[r].provider] ?? []).map((m) => (
												<option key={m} value={m} />
											))}
										</datalist>
										{roles[r].provider === 'local' ? (
											<div class="row small">
												fallback:
												<select value={roles[r].fallbackProvider ?? 'none'} onChange={(e) => setRoles({ ...roles, [r]: { ...roles[r], fallbackProvider: e.currentTarget.value } })}>
													{PROVIDERS.filter(([v]) => !['local', 'demo'].includes(v)).map(([v, l]) => (
														<option key={v} value={v}>
															{l}
														</option>
													))}
												</select>
												<input value={roles[r].fallbackModel ?? ''} onInput={(e) => setRoles({ ...roles, [r]: { ...roles[r], fallbackModel: e.currentTarget.value } })} placeholder="fallback model" />
											</div>
										) : null}
									</td>
									<td>
										<Button small variant="ghost" onClick={async () => { await save(); const t = await sock.gmOp('test_role', { role: r }); toast(t.ok ? `${r}: valid JSON came back` : `${r}: test failed`, t.ok ? 'ok' : 'error'); }}>
											Test
										</Button>{' '}
										{gm.settings.llm.roles[r].lastTest ? <Pill tone={gm.settings.llm.roles[r].lastTest.ok ? 'ok' : 'bad'}>{gm.settings.llm.roles[r].lastTest.ok ? 'ok' : 'failed'}</Pill> : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
					<Field label="Quick decisions (routing, NPC reactions, pivots, consent replies, moderation)">
						<select value={decider} onChange={(e) => setDecider(e.currentTarget.value)}>
							<option value="jev">Jev on Workers AI, then Clef-flash, then the Adjudicator's model</option>
							<option value="clef">Clef-flash, then the Adjudicator's model</option>
							<option value="chat">The Adjudicator's model only</option>
							<option value="off">Off (engine defaults)</option>
						</select>
					</Field>
					<p class="small muted">
						A small, fast model suits the Interpreter; a stronger one suits the Adjudicator and Planner. Tokens used: {gm.tokensUsed.toLocaleString()} of {gm.settings.tokenBudget.toLocaleString()}.
					</p>
				</Card>
				<Card title="LLM inspector">
					<table class="table small">
						<thead>
							<tr>
								<th>Beat</th>
								<th>Role</th>
								<th>Area</th>
								<th>OK</th>
								<th>Tokens</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{gm.inspector.map((i: any) => (
								<tr key={i.id}>
									<td>{i.beat}</td>
									<td>{i.role}</td>
									<td>{i.area ?? ''}</td>
									<td>{i.ok ? '✓' : '✗'}</td>
									<td>
										{i.tokens_in}/{i.tokens_out}
									</td>
									<td>
										<button class="linkish" onClick={() => sock.gmOp('inspector', { id: i.id }).then(setInspect)}>
											inspect
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</Card>
			</div>
			<div class="gm-side">
				<Card title="Local worker">
					<p class="small">
						Status: <Pill tone={w.status === 'online' ? 'ok' : w.status === 'idle' ? 'accent' : 'muted'}>{w.configured ? w.status : 'not set up'}</Pill>
						{w.lastPoll ? ` · last poll ${timeAgo(w.lastPoll)}` : ''}
					</p>
					<Workers
						status={w}
						campaignId={gm.settings.campaignId}
						add={(name) => sock.gmOp('worker_add', { name })}
						revoke={(id) => sock.gmOp('worker_revoke', { id })}
					/>
					<p class="small muted">
						Run it with <code>npx @saga/local-worker</code> (or <code>node saga-local-worker.mjs</code>) and open <code>http://127.0.0.1:4477</code>.
					</p>
					{w.recent?.length ? (
						<ul class="list small">
							{w.recent.map((j: any) => (
								<li key={j.id}>
									{j.role} <Pill tone={j.status === 'done' ? 'ok' : j.status === 'failed' ? 'bad' : 'accent'}>{j.status}</Pill> {j.workerName ?? ''} {j.error ? <span class="bad">{j.error}</span> : null}
								</li>
							))}
						</ul>
					) : null}
				</Card>
			</div>
			{inspect ? (
				<Modal title={`${inspect.role} call, beat ${inspect.beat}`} onClose={() => setInspect(null)} wide>
					<p class="small">
						{inspect.record.provider} / {inspect.record.model} · {inspect.record.ms} ms · {inspect.record.usage.inputTokens} in / {inspect.record.usage.outputTokens} out {inspect.record.usage.cachedTokens ? `(${inspect.record.usage.cachedTokens} cached)` : ''}
					</p>
					{inspect.record.errors.length ? <p class="bad small">Validation: {inspect.record.errors.join(' | ')}</p> : null}
					<h4>Context packet</h4>
					{inspect.record.messages.map((m: any, i: number) => (
						<details key={i} open={i === inspect.record.messages.length - 1}>
							<summary>{m.role}</summary>
							<pre class="mono small">{m.content}</pre>
						</details>
					))}
					<h4>Raw response{inspect.record.responses.length > 1 ? 's' : ''}</h4>
					{inspect.record.responses.map((r: string, i: number) => (
						<pre key={i} class="mono small">
							{r}
						</pre>
					))}
				</Modal>
			) : null}
		</div>
	);
}

// ------------------------------------------------------------------ settings, directory, rewind

function Settings(p: { sock: GameSocket; gm: any }) {
	const { gm, sock } = p;
	const [s, setS] = useState({ ...gm.settings });
	const bans = useLoad(() => get(`/api/games/${gm.settings.code}/bans`));
	const [dir, setDir] = useState({ listing: 'public', password: '', approval: gm.approvalRequired, tags: '' });
	const [beat, setBeat] = useState(Math.max(0, gm.clock.beat - 1));
	const set = (k: string, v: unknown) => setS({ ...s, [k]: v });
	const check = (k: string, label: string) => (
		<label class="check">
			<input type="checkbox" checked={!!s[k]} onChange={(e) => set(k, e.currentTarget.checked)} /> {label}
		</label>
	);
	const save = () => {
		const { llm: _l, ...rest } = s;
		return sock.gmOp('settings', { patch: rest }).then(() => toast('Settings saved.', 'ok'));
	};
	return (
		<div class="gm-grid">
			<div class="gm-main">
				<Card title="Play" actions={<Button small onClick={save}>Save</Button>}>
					<div class="row wrap">
						<Field label="Name">
							<input value={s.name} onInput={(e) => set('name', e.currentTarget.value)} />
						</Field>
						<Field label="Seats">
							<input type="number" min={1} max={8} value={s.seats} onInput={(e) => set('seats', Number(e.currentTarget.value))} />
						</Field>
						<Field label="Action timer (s)">
							<input type="number" min={10} max={600} value={s.timerSeconds} onInput={(e) => set('timerSeconds', Number(e.currentTarget.value))} />
						</Field>
						<Field label="Max time per beat (s, 0 = off)">
							<input type="number" min={0} value={s.maxBeatSeconds} onInput={(e) => set('maxBeatSeconds', Number(e.currentTarget.value))} />
						</Field>
					</div>
					{check('typingResets', 'Typing resets the timer to full')}
					{check('observingAllowed', 'Players may observe others')}
					{check('allowOriginalCharacters', 'Players may create original characters')}
					{check('approveOriginalCharacters', 'Original characters need my approval')}
					{check('approvalMode', 'Approval mode: Adjudicator outcomes wait for me')}
					{check('handMode', 'Hand mode: players hold three cards and choose')}
					{check('allowCreateItem', 'The Adjudicator may create improvisable items')}
					<div class="row wrap">
						<Field label="Consent timeout">
							<select value={s.consentTimeout} onChange={(e) => set('consentTimeout', e.currentTarget.value)}>
								<option value="decline">counts as decline</option>
								<option value="accept">counts as accept</option>
							</select>
						</Field>
						<Field label="Switching characters">
							<select value={s.switching} onChange={(e) => set('switching', e.currentTarget.value)}>
								<option value="off">off</option>
								<option value="approve">I approve each switch</option>
								<option value="auto">automatic</option>
							</select>
						</Field>
						<Field label="Improvised NPCs">
							<select value={s.improvisation} onChange={(e) => set('improvisation', e.currentTarget.value)}>
								<option value="off">off</option>
								<option value="low">low</option>
								<option value="high">high</option>
							</select>
						</Field>
						<Field label="Living story re-plans">
							<select value={s.replan} onChange={(e) => set('replan', e.currentTarget.value)}>
								<option value="off">off</option>
								<option value="approval">need my approval</option>
								<option value="auto">apply automatically</option>
							</select>
						</Field>
						<Field label="When a player leaves">
							<select value={s.leaveMode} onChange={(e) => set('leaveMode', e.currentTarget.value)}>
								<option value="npc">character becomes an NPC</option>
								<option value="step_away">character steps away (unclaimed)</option>
							</select>
						</Field>
						<Field label="Newcomers arrive in">
							<select value={s.joinArea ?? ''} onChange={(e) => set('joinArea', e.currentTarget.value || null)}>
								<option value="">the story's join point</option>
								{gm.spec.areas.map((a: any) => (
									<option key={a.id} value={a.id}>
										{a.name}
									</option>
								))}
							</select>
						</Field>
					</div>
					<div class="row wrap">
						<Field label="Content rating">
							<select value={s.contentRating} onChange={(e) => set('contentRating', e.currentTarget.value)}>
								{['G', 'PG', 'PG-13', 'R'].map((r) => (
									<option key={r}>{r}</option>
								))}
							</select>
						</Field>
						<Field label="Token budget (hard cap)">
							<input type="number" value={s.tokenBudget} onInput={(e) => set('tokenBudget', Number(e.currentTarget.value))} />
						</Field>
					</div>
					<Field label="Lines and veils" hint="Goes into every Narrator contract.">
						<textarea rows={2} value={s.linesAndVeils} onInput={(e) => set('linesAndVeils', e.currentTarget.value)} />
					</Field>
					<Field label="Locked (ids the Planner must not touch, comma separated)">
						<input value={s.locked.join(', ')} onInput={(e) => set('locked', e.currentTarget.value.split(',').map((x) => x.trim()).filter(Boolean))} />
					</Field>
				</Card>
			</div>
			<div class="gm-side">
				<Card title="Listing and access">
					<Field label="Listing">
						<select value={dir.listing} onChange={(e) => setDir({ ...dir, listing: e.currentTarget.value })}>
							<option value="public">Public</option>
							<option value="unlisted">Unlisted</option>
						</select>
					</Field>
					<Field label="New password (blank = none)">
						<input type="password" value={dir.password} onInput={(e) => setDir({ ...dir, password: e.currentTarget.value })} />
					</Field>
					<label class="check">
						<input type="checkbox" checked={dir.approval} onChange={(e) => setDir({ ...dir, approval: e.currentTarget.checked })} /> Approve each join
					</label>
					<Field label="Tags">
						<input value={dir.tags} onInput={(e) => setDir({ ...dir, tags: e.currentTarget.value })} placeholder="horror, beginner" />
					</Field>
					<Button
						small
						onClick={() =>
							patch(`/api/games/${gm.settings.code}/directory`, { listing: dir.listing, password: dir.password || null, approval: dir.approval, ...(dir.tags ? { tags: dir.tags.split(',').map((t) => t.trim()) } : {}) }).then(() => {
								toast('Listing updated.', 'ok');
								sock.gmOp('state');
							})
						}
					>
						Update
					</Button>
				</Card>
				<Card title="Bans">
					{bans.data?.bans?.length ? (
						<ul class="list small">
							{bans.data.bans.map((b: any) => (
								<li key={b.user_id} class="row-between">
									{b.display_name}
									<button class="linkish" onClick={() => del(`/api/games/${gm.settings.code}/bans/${b.user_id}`).then(bans.reload)}>
										lift
									</button>
								</li>
							))}
						</ul>
					) : (
						<Empty>No bans.</Empty>
					)}
				</Card>
				<Card title="Rewind">
					<p class="small">Roll the whole campaign back by replaying the log to an earlier beat. The full log is archived first.</p>
					<div class="row">
						<input type="number" min={0} max={gm.clock.beat} value={beat} onInput={(e) => setBeat(Number(e.currentTarget.value))} class="short" />
						<Button small variant="danger" onClick={() => confirm(`Rewind to beat ${beat}?`) && sock.gmOp('rewind', { beat })}>
							Rewind
						</Button>
					</div>
					<p class="small muted">Now at beat {gm.clock.beat}. Spec version {gm.spec.version}.</p>
					{gm.settings.templateId ? (
						<Button small variant="ghost" onClick={() => post(`/api/games/${gm.settings.code}/upgrade`, {}).then(() => toast('Upgraded to your library’s template version.', 'ok'), (e) => toast(e.message, 'error'))}>
							Upgrade to my library’s template version
						</Button>
					) : null}
					<a class="small" href={`/api/games/${gm.settings.code}/log`} target="_blank" rel="noopener">
						Export event log
					</a>
				</Card>
			</div>
		</div>
	);
}

// ------------------------------------------------------------------ art

function Art(p: { sock: GameSocket; gm: any }) {
	const { gm, sock } = p;
	const [target, setTarget] = useState<{ kind: string; id: string; state?: string } | null>(null);
	const [variantArea, setVariantArea] = useState(gm.spec.areas[0]?.id ?? '');
	const [variantState, setVariantState] = useState('lit=true');
	const slots = [
		{ kind: 'cover', id: 'cover', label: 'Campaign cover', asset: gm.spec.cover },
		...gm.spec.areas.flatMap((a: any) => [
			{ kind: 'area', id: a.id, label: `Area: ${a.name}`, asset: a.asset },
			...Object.entries(a.stateAssets ?? {}).map(([st, asset]) => ({ kind: 'area', id: a.id, state: st, label: `Area: ${a.name} when ${st}`, asset })),
		]),
		...gm.characters.filter((c: any) => !c.claimedBy || true).map((c: any) => ({ kind: 'character', id: c.id, label: `Portrait: ${c.name}`, asset: c.asset })),
		...gm.spec.endings.map((e: any) => ({ kind: 'ending', id: e.id, label: `Ending: ${e.title}`, asset: e.asset })),
		...gm.spec.arcs.map((a: any) => ({ kind: 'arc', id: a.id, label: `Arc completion: ${a.name}`, asset: a.completion?.asset })),
	];
	return (
		<div class="gm-grid">
			<div class="gm-main">
				<Card title="Art slots">
					<p class="small muted">Anything without art falls back to the default theme. Drag an image from the library onto a slot, or pick a slot and press “Use”.</p>
					<div class="slot-grid">
						{slots.map((s) => (
							<div
								key={`${s.kind}:${s.id}:${(s as any).state ?? ''}`}
								class={`slot ${target?.kind === s.kind && target?.id === s.id && target?.state === (s as any).state ? 'slot-on' : ''}`}
								onClick={() => setTarget({ kind: s.kind, id: s.id, state: (s as any).state })}
								onDragOver={(e) => e.preventDefault()}
								onDrop={(e) => {
									e.preventDefault();
									const asset = e.dataTransfer?.getData('text/asset');
									if (asset) sock.gmOp('set_asset', { target: { kind: s.kind, id: s.id, state: (s as any).state }, asset });
								}}
							>
								{s.asset ? <img src={media(s.asset, 320)} alt="" /> : <div class="slot-empty">no art</div>}
								<span class="small">{s.label}</span>
								{s.asset ? (
									<button
										class="linkish small"
										onClick={(e) => {
											e.stopPropagation();
											sock.gmOp('set_asset', { target: { kind: s.kind, id: s.id, state: (s as any).state }, asset: null });
										}}
									>
										clear
									</button>
								) : null}
							</div>
						))}
					</div>
					<h4>Area variant by state</h4>
					<p class="small muted">Backgrounds can switch with area state (night, flooded, burning). Add a slot, then assign art to it.</p>
					<div class="row wrap">
						<select value={variantArea} onChange={(e) => setVariantArea(e.currentTarget.value)}>
							{gm.spec.areas.map((a: any) => (
								<option key={a.id} value={a.id}>
									{a.name}
								</option>
							))}
						</select>
						<input value={variantState} onInput={(e) => setVariantState(e.currentTarget.value)} placeholder="key=value, e.g. flooded=true" />
						<Button small variant="ghost" disabled={!/^[^=]+=.+$/.test(variantState)} onClick={() => setTarget({ kind: 'area', id: variantArea, state: variantState })}>
							Select this variant slot
						</Button>
					</div>
					{target?.state ? <p class="small">Selected: {target.id} when {target.state}. Press “Use” on an image.</p> : null}
				</Card>
			</div>
			<div class="gm-side">
				<AssetLibrary pick={target ? (id) => sock.gmOp('set_asset', { target, asset: id }).then(() => toast('Art assigned.', 'ok')) : undefined} />
			</div>
		</div>
	);
}
